/*
 * Copyright 2025 ixPilot Teknoloji
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Divider,
  useToast,
  Spinner,
  Icon,
} from "@chakra-ui/react";
import { FiCalendar, FiUser, FiArrowRight } from "react-icons/fi";
import api from "utils/api";
import { formatDateOnly } from "utils/dateFormatter";

const AbonelikDetayModal = ({ isOpen, onClose, abonelik }) => {
  const toast = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Plan değerini formatla (örn: "12" -> "12 Ay")
  const formatPlan = (tur) => {
    if (!tur) return "-";
    const planLabels = {
      "1": "1 Ay",
      "3": "3 Ay",
      "12": "12 Ay",
      "24": "24 Ay",
    };
    return planLabels[tur] || `${tur} Ay`;
  };

  const deviceId = abonelik?.cihaz_id || abonelik?.id;

  const loadHistory = async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/devices/${deviceId}/abonelik-gecmisi`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Abonelik geçmişi alınamadı.");
      }
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setHistory([]);
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && deviceId) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deviceId]);

  const statusValue = abonelik?.dongu_durumu_raw ?? abonelik?.dongu_durumu;

  const timeline = useMemo(() => {
    const steps = [];
    
    // Geçmiş kayıtları tarihe göre sırala (en eskiden en yeniye)
    const sortedHistory = [...history].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    
    // İlk kayıt için: Geçmişte en eski kayıt varsa onun onceki_tur değerini kullan
    // Yoksa mevcut abonelik.tur değerini kullan
    let ilkKayitTur = abonelik?.tur;
    if (sortedHistory.length > 0) {
      // En eski kayıtın onceki_tur değeri ilk aboneliğin tur değeridir
      const enEskiKayit = sortedHistory[0];
      if (enEskiKayit.onceki_tur) {
        ilkKayitTur = enEskiKayit.onceki_tur;
      }
    }
    
    if (abonelik) {
      const initialStart =
        abonelik.satis_tarihi_raw ||
        abonelik.satis_tarihi ||
        abonelik.yenilendigi_tarih_raw ||
        abonelik.yenilendigi_tarih;
      
      // İlk kayıt için geçmişten onceki_tur varsa onu kullan, yoksa mevcut tur'u kullan
      let ilkDonguTarihi = abonelik.dongu_tarihi_raw || abonelik.dongu_tarihi;
      let ilkDonguSayisi = abonelik.dongu_sayisi;
      
      // Eğer geçmiş kayıt varsa, en eski kayıtın onceki değerlerini kullan
      if (sortedHistory.length > 0) {
        const enEskiKayit = sortedHistory[0];
        if (enEskiKayit.onceki_dongu_tarihi) {
          ilkDonguTarihi = enEskiKayit.onceki_dongu_tarihi;
        }
        if (enEskiKayit.onceki_dongu_sayisi !== null && enEskiKayit.onceki_dongu_sayisi !== undefined) {
          ilkDonguSayisi = enEskiKayit.onceki_dongu_sayisi;
        }
      }
      
      steps.push({
        key: "initial",
        label: "İlk Kayıt",
        yenilendigi_tarih: initialStart,
        dongu_tarihi: ilkDonguTarihi,
        tur: ilkKayitTur,
        dongu_sayisi: ilkDonguSayisi,
        aciklama: "İlk abonelik kaydı",
        islem_yapan: abonelik.firma,
      });
    }
    
    // Geçmiş kayıtları ekle
    sortedHistory.forEach((item, idx) => {
      steps.push({
        key: item.id,
        label: idx === sortedHistory.length - 1 ? "Son Yenileme" : "Yenileme",
        yenilendigi_tarih: item.yeni_yenilendigi_tarih || item.onceki_yenilendigi_tarih,
        dongu_tarihi: item.yeni_dongu_tarihi,
        tur: item.yeni_tur,
        dongu_sayisi: item.yeni_dongu_sayisi,
        aciklama: item.aciklama,
        islem_yapan: item.islem_yapan,
        date: item.created_at,
      });
    });
    return steps;
  }, [abonelik, history]);


  if (!abonelik) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Abonelik Detayı
            </Text>
            <Text fontSize="xs" color="gray.600">
              {abonelik.plaka || "-"} • {abonelik.imei || "-"}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="600px" overflowY="auto">
          <VStack align="stretch" spacing={4}>
            <Box
              bg="white"
              borderRadius="xl"
              shadow="sm"
              p={4}
              border="1px solid"
              borderColor="gray.100"
            >
              <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Durum
                  </Text>
                  <Badge
                    colorScheme={statusValue === "aktif" ? "green" : "red"}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    {statusValue === "aktif" ? "Aktif" : "Pasif"}
                  </Badge>
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Satış Türü
                  </Text>
                  <Text fontWeight="semibold">
                    {abonelik.satis_turu === "Satılık" ? "Satış" : 
                     abonelik.satis_turu === "Kiralık" ? "Kiralama" : 
                     abonelik.satis_turu || "-"}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Yenilendiği Tarih
                  </Text>
                  <Text fontWeight="semibold">
                    {formatDateOnly(abonelik.yenilendigi_tarih_raw || abonelik.yenilendigi_tarih)}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Mevcut Bitiş
                  </Text>
                  <Text fontWeight="semibold">
                    {formatDateOnly(abonelik.dongu_tarihi_raw || abonelik.dongu_tarihi)}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Plan
                  </Text>
                  <Text fontWeight="semibold">{formatPlan(abonelik.tur)}</Text>
                </VStack>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Döngü Sayısı
                  </Text>
                  <Text fontWeight="semibold">{abonelik.dongu_sayisi ?? "-"}</Text>
                </VStack>
              </HStack>
            </Box>

            <Box
              bg="white"
              borderRadius="xl"
              shadow="sm"
              p={4}
              border="1px solid"
              borderColor="gray.100"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Icon as={FiCalendar} />
                  <Text fontWeight="semibold">Abonelik Zaman Çizelgesi</Text>
                </HStack>
              </HStack>

              {loading ? (
                <VStack py={6}>
                  <Spinner />
                  <Text fontSize="sm" color="gray.500">
                    Geçmiş yükleniyor...
                  </Text>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {timeline.map((step, index) => (
                    <Box
                      key={step.key || index}
                      border="1px solid"
                      borderColor="gray.100"
                      borderRadius="lg"
                      p={3}
                      bg={index === 0 ? "gray.50" : "white"}
                    >
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="flex-start" spacing={1}>
                          <Badge colorScheme={index === 0 ? "gray" : "blue"}>
                            {step.label}
                          </Badge>
                          <HStack spacing={2} color="gray.600" fontSize="sm">
                            <Icon as={FiArrowRight} />
                            <Text>
                              {formatDateOnly(step.yenilendigi_tarih)} → {formatDateOnly(step.dongu_tarihi)}
                            </Text>
                          </HStack>
                        </VStack>
                        <VStack align="flex-end" spacing={1}>
                          <Text fontSize="sm">
                            Plan: <strong>{formatPlan(step.tur)}</strong>
                          </Text>
                          <Text fontSize="sm">
                            Döngü: <strong>{step.dongu_sayisi ?? "-"}</strong>
                          </Text>
                        </VStack>
                      </HStack>
                      <Divider my={2} />
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          {step.aciklama || "Bilgi yok"}
                        </Text>
                        {step.islem_yapan && (
                          <HStack spacing={1} fontSize="sm" color="gray.500">
                            <Icon as={FiUser} />
                            <Text>{step.islem_yapan}</Text>
                          </HStack>
                        )}
                      </HStack>
                    </Box>
                  ))}
                  {timeline.length === 0 && (
                    <Text fontSize="sm" color="gray.500">
                      Geçmiş kaydı bulunamadı.
                    </Text>
                  )}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" onClick={onClose} size="sm">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AbonelikDetayModal;

